import React, { useMemo } from "react";
import PropTypes from "prop-types";
import SignaturePad from "../../../../components/UI/SignaturePad";
import useCurrentUser from "../../../../hooks/useCurrentUser";
import {
  displayDateUS,
  formatSignature,
} from "../../../../utils/dateUtils";
import { PATIENT_THEME } from "../../patientTheme";
import { PenIcon, UserIcon } from "../../../../Icons";

const labelClass =
  "block text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-0.5";
const fieldClass = `w-full h-7 px-2 text-[12px] text-slate-900 border border-slate-200 rounded bg-white outline-none disabled:bg-slate-50 disabled:text-slate-500 ${PATIENT_THEME.fieldFocus}`;
/** Read-only value box, used for auto-filled names and stamped dates. */
const readOnlyBox =
  "h-7 flex items-center px-2 text-[12px] font-semibold text-slate-800 bg-indigo-50/70 border border-indigo-100 rounded";

/** Time-of-day stamp shown beside a signature, e.g. "08:10:38 AM PST". */
const pstStamp = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(date);
  return `${time} PST`;
};

const Section = ({ icon: Icon, title, children }) => (
  <section className="rounded-md border border-slate-200 overflow-hidden">
    <div
      className={`flex items-center gap-1.5 px-3 py-1.5 border-b ${PATIENT_THEME.section.head}`}
    >
      {Icon && <Icon size={12} className={PATIENT_THEME.section.icon} />}
      <h3
        className={`text-[10px] font-semibold uppercase tracking-wide ${PATIENT_THEME.section.title}`}
      >
        {title}
      </h3>
    </div>
    <div className="p-3">{children}</div>
  </section>
);

Section.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const Field = ({ label, children }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

Field.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

/**
 * One-click typed e-sign block used by clinician / employee / office sections.
 */
export const ElectronicAuthSection = ({
  title,
  nameLabel = "Signing Clinician",
  signerName = "",
  signatureText = "",
  canSign = false,
  printMode = false,
  onSign,
  onClear,
  error = "",
}) => {
  const hasSignature = Boolean(signatureText);
  const applySignature = () => {
    if (!signerName) return;
    onSign?.(formatSignature(signerName), signerName);
  };

  return (
    <Section icon={PenIcon} title={title}>
      <Field label={nameLabel}>
        <div
          className={`${readOnlyBox} max-w-md ${
            signerName ? "" : "text-slate-400 font-normal italic"
          }`}
        >
          {signerName || "Pending name…"}
        </div>
      </Field>

      <div className="mt-3 pt-3 border-t border-slate-200">
        <p className="text-[11px] font-semibold text-slate-700 mb-1.5">
          This form has been electronically signed by:
        </p>
        {hasSignature ? (
          <div>
            <div
              className="border-b border-slate-400 min-h-[28px] pb-1 text-[12px] font-semibold text-slate-800"
              title={signatureText}
            >
              {signatureText}
            </div>
            {canSign && !printMode ? (
              <button
                type="button"
                onClick={() => onClear?.()}
                className="mt-1 text-[9px] font-semibold text-indigo-600 hover:underline"
              >
                Re-sign
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <div className="border-b border-slate-400 min-h-[28px] text-slate-300 tracking-widest text-[11px]">
              ________________________________
            </div>
            {!printMode && canSign ? (
              <>
                <p className="text-[11px] text-slate-600 mt-2">
                  Please click the button below to electronically sign this
                  form.
                </p>
                <button
                  type="button"
                  onClick={applySignature}
                  disabled={!signerName}
                  className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 text-[11px] font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  <PenIcon size={12} />
                  E-Sign Form
                </button>
              </>
            ) : (
              <p className="text-[11px] text-slate-500 mt-2 italic">
                Pending electronic signature
              </p>
            )}
          </>
        )}
        {error ? (
          <p className="mt-1.5 text-[10px] font-medium text-rose-600">{error}</p>
        ) : null}
      </div>
    </Section>
  );
};

ElectronicAuthSection.propTypes = {
  title: PropTypes.string.isRequired,
  nameLabel: PropTypes.string,
  signerName: PropTypes.string,
  signatureText: PropTypes.string,
  canSign: PropTypes.bool,
  printMode: PropTypes.bool,
  onSign: PropTypes.func,
  onClear: PropTypes.func,
  error: PropTypes.string,
};

/**
 * Signature sections for patient clinical forms.
 *
 * Order forms (NOA / Physician Order) pass `showPatientSection={false}`.
 *
 * Clinician Authentication: one field (Signing Clinician, auto from login) +
 * the Application electronic-signature message. One click auto-signs.
 * Same layout on screen, print, and PDF.
 */
const FormSignatureSections = ({
  patientNameValue,
  onPatientNameChange,
  patientSignatureUrl,
  patientSignedAt,
  patientPadRef,
  onPatientDraw,
  onPatientClear,
  clinicianNameValue,
  clinicianSignatureText,
  clinicianSignedAt,
  onClinicianSign,
  onClinicianClear,
  canSign = false,
  printMode = false,
  showPatientSection = true,
  showClinicianSection = true,
  patientSectionTitle = "Patient Signature Information",
  clinicianSectionTitle = "Clinician Authentication",
  patientNameDisabled = false,
}) => {
  const { fullName } = useCurrentUser();

  // Falls back to the logged-in user so the clinician never types their name.
  // Never on paper though — whoever prints an unsigned form is not its signer.
  const signerName = clinicianNameValue || (printMode ? "" : fullName) || "";

  // Until the patient actually signs, the stamp previews the current time
  // rather than sitting empty. Frozen per mount so it does not tick.
  const openedAt = useMemo(() => new Date().toISOString(), []);
  const patientStampedAt = patientSignedAt || (printMode ? null : openedAt);

  return (
    <>
      {showPatientSection ? (
        <Section icon={UserIcon} title={patientSectionTitle}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2">
              <label className={labelClass}>Patient Electronic Signature</label>
              {printMode ? (
                <div className="h-[104px] flex items-end justify-center rounded border border-slate-200 px-3 pb-2">
                  {patientSignatureUrl ? (
                    <img
                      src={patientSignatureUrl}
                      alt="Patient signature"
                      className="max-h-[92px] max-w-full object-contain"
                    />
                  ) : (
                    <div className="w-full border-b border-slate-400" />
                  )}
                </div>
              ) : (
                <>
                  <SignaturePad
                    ref={patientPadRef}
                    value={patientSignatureUrl || undefined}
                    disabled={!canSign}
                    height={104}
                    onChange={onPatientDraw}
                    onClear={onPatientClear}
                    placeholder="Ask the patient to sign here"
                  />
                  <p className="mt-1 text-[9px] text-slate-400">
                    Captured as an image and stored securely with the patient
                    record.
                  </p>
                </>
              )}
            </div>
            <div className="space-y-2">
              <Field label="Patient / Representative Name">
                <input
                  type="text"
                  className={fieldClass}
                  value={patientNameValue || ""}
                  disabled={!canSign || patientNameDisabled}
                  placeholder="Full name"
                  onChange={(event) => onPatientNameChange?.(event.target.value)}
                />
              </Field>
              <Field label="Signed Date">
                <div className={readOnlyBox}>
                  {displayDateUS(patientStampedAt) || "—"}
                </div>
              </Field>
              <Field label="Time Stamp">
                <div className={readOnlyBox}>
                  {pstStamp(patientStampedAt) || "—"}
                </div>
              </Field>
            </div>
          </div>
        </Section>
      ) : null}

      {showClinicianSection ? (
        <ElectronicAuthSection
          title={clinicianSectionTitle}
          signerName={signerName}
          signatureText={clinicianSignatureText}
          canSign={canSign}
          printMode={printMode}
          onSign={onClinicianSign}
          onClear={onClinicianClear}
        />
      ) : null}
    </>
  );
};

FormSignatureSections.propTypes = {
  patientNameValue: PropTypes.string,
  onPatientNameChange: PropTypes.func,
  patientSignatureUrl: PropTypes.string,
  patientSignedAt: PropTypes.string,
  patientPadRef: PropTypes.object,
  onPatientDraw: PropTypes.func,
  onPatientClear: PropTypes.func,
  clinicianNameValue: PropTypes.string,
  clinicianSignatureText: PropTypes.string,
  clinicianSignedAt: PropTypes.string,
  onClinicianSign: PropTypes.func,
  onClinicianClear: PropTypes.func,
  onClinicianDateChange: PropTypes.func,
  canSign: PropTypes.bool,
  printMode: PropTypes.bool,
  showPatientSection: PropTypes.bool,
  showClinicianSection: PropTypes.bool,
  patientSectionTitle: PropTypes.string,
  clinicianSectionTitle: PropTypes.string,
  patientNameDisabled: PropTypes.bool,
};

export default FormSignatureSections;
